import AcceleratorDoc, {
  AcceleratorDocStoreKey,
} from "../models/AcceleratorDoc";
import DocTypeConfig from "../models/config/DocTypeConfig";
import { ExtractedTable } from "../models/ExtractedTable";
import { BaseField } from "../models/extraction/BaseField";
import { DlfExtractor } from "../services/extraction/DlfExtractor";
import { ExtractedFieldData } from "../models/extraction/ExtractedFieldData";
import { ExtractedTableData } from "../models/extraction/ExtractedTableData";
import { TableExtractor } from "../services/extraction/TableExtractor";
import { DlfMatcher } from "../models/matchers/DlfMatcher";
import Model from "../models/Model";
import { TableModel } from "../models/TableModel";
import { TableConfig } from "../models/config/TableConfig";
import { DocFieldConfig } from "../models/config/DocFieldConfig";

export { AcceleratorDocExtractor };

class AcceleratorDocExtractor {
  /**
   * Model AcceleratorDoc will be extracted against.
   */
  _model: Model;

  _acceleratorDoc: AcceleratorDoc;

  _docTypeConfig: DocTypeConfig;

  _acceleratorOcrThresholds: boolean;

  /**
   * When false, a 404 from a matcher/table extraction call will not mark the batch as stale.
   * Used when extraction is triggered as a direct, self-initiated preview of the current user's
   * own unsaved template edit, where a 404 does not indicate the batch changed externally.
   */
  _treatNotFoundAsStale: boolean;

  constructor(
    acceleratorDoc: AcceleratorDoc,
    model: Model,
    docTypeConfig: DocTypeConfig,
    acceleratorOcrThresholds: boolean,
    treatNotFoundAsStale: boolean = true,
  ) {
    this._model = model;
    this._acceleratorDoc = acceleratorDoc;
    this._docTypeConfig = docTypeConfig;
    this._acceleratorOcrThresholds = acceleratorOcrThresholds;
    this._treatNotFoundAsStale = treatNotFoundAsStale;
  }

  async extract(): Promise<boolean> {
    // update modelName so Document can at least display the model thats editing it
    this._acceleratorDoc.modelName = this._model.storeId.name;
    this._acceleratorDoc.hasModelChanged = this._model.changed;
    // update doc type
    this._acceleratorDoc.changeDocType(this._docTypeConfig, this._model);

    /// ... rerun extraction ...
    await this.runRequiredExtraction();

    // finally update document statuses
    this._acceleratorDoc.calculateDocumentStatusWithOcr(
      this._acceleratorOcrThresholds,
      this._model,
      this._docTypeConfig,
    );

    return true;
  }

  /**
   * Runs Extraction for each DLF and Table using the provided Models configured Matchers,
   * unless it has already been extracted
   */
  async runRequiredExtraction() {
    for (const dlfConfig of this._docTypeConfig.fields) {
      await this.extractField(dlfConfig);
    }
    for (const tableConfig of this._docTypeConfig.tables) {
      await this.extractTable(tableConfig);
    }
  }

  private async extractField(dlf: DocFieldConfig) {
    // console.debug(`Checking if extraction should be run for DLF:${dlf.fieldName}`);
    // console.debug("Get Matcher for DLF");
    const extractedFieldData: ExtractedFieldData | undefined =
      this._acceleratorDoc.getExtractedFieldData(dlf.fieldName);
    const dlfMatcher: DlfMatcher | undefined = this._model.getDlfMatcher(dlf);
    if (!dlfMatcher || !extractedFieldData) {
      return;
    }
    // check if theres anything in the dlf map that contains the current matcher

    // extracted previously, doesn't require reextraction
    if (
      extractedFieldData.extraction &&
      extractedFieldData!.extraction.extractedBy(dlfMatcher) &&
      !extractedFieldData.extraction._requireExtract
    ) {
      // console.debug(`DLF ${dlf.fieldName} has already been extracted and set against the extraction result`);
      extractedFieldData.extraction.updateFieldStatusFromAllowEmpty(dlfMatcher);
      return;
    }

    // use getReuse to ignore possibly failed getExtracted()
    const reuseBaseFields = extractedFieldData.getReuse().slice(1);
    const reuseForMatcherIdx = reuseBaseFields.findIndex(
      (baseField) => baseField && baseField.extractedBy(dlfMatcher),
    );
    if (reuseForMatcherIdx > -1) {
      // console.debug(`DLF ${dlf.fieldName} has already been extracted by Reuse Result #${reuseForMatcherIdx + 1}`);
      // overwrite to avoid reuse index check
      extractedFieldData.setIndex(reuseForMatcherIdx + 1);
      extractedFieldData.extraction?.updateFieldStatusFromAllowEmpty(
        dlfMatcher,
      );
      return;
    }

    // skip that check for now, just rerun extraction !
    let field: BaseField | undefined;
    try {
      field = await new DlfExtractor(
        dlfMatcher,
        this._treatNotFoundAsStale,
      ).extract(this._acceleratorDoc);
    } catch (e) {
      console.error(e);
    }

    if (!field) {
      // console.warn(`Could not extract DLF ${dlfMatcher.fieldName} using matcher ${dlfMatcher.getMatcherId()}`)
      return;
    }

    // console.debug(`Extracted DLF ${dlfMatcher.fieldName} as ${JSON.stringify(field, undefined, 2)}`)
    this._acceleratorDoc.updateExtractedField(field);
  }
  private async extractTable(tableConfig: TableConfig) {
    const tableModel: TableModel | undefined = this._model.getTableModel(
      tableConfig.name,
    );
    if (!tableModel) return;

    // first check current Extraction
    const extractedTableData = this._acceleratorDoc.getExtractedTableData(
      tableConfig.name,
    );
    if (!extractedTableData) {
      console.debug("No extracted data exists for table " + tableConfig.name);
      return;
    }

    if (
      extractedTableData.extraction &&
      extractedTableData.extraction.extractedBy(tableModel)
    ) {
      console.log(
        `TABLE ${tableConfig.name} has already been extracted and set against the extraction result`,
      );
      return;
    }

    // check reuse cache
    // check reuse index
    let reuseTable: ExtractedTable | undefined;
    const reuseForMatcherIdx = extractedTableData.findReuseIndex(tableModel);

    if (reuseForMatcherIdx != -1) {
      console.log("TableModel in reuse array");
      const newTableModel = extractedTableData.getReuseAt(
        reuseForMatcherIdx + 1,
      )!;
      reuseTable = extractedTableData.reuseToExtract(
        new TableModel(newTableModel),
      );
      console.log(
        "TableModel in reuse array cached extracted table " + reuseTable,
      );
      if (reuseTable) {
        extractedTableData.put(reuseTable);
        extractedTableData.setIndex(reuseForMatcherIdx + 1);
        return;
      }
    }

    console.debug(
      "Could not find reuse option for model table from reuse array",
    );
    // check reuse cache for table
    reuseTable = extractedTableData.fromCache(tableModel);
    if (reuseTable) {
      console.debug("Got reuse table model from reuse cache");
      this._acceleratorDoc.updateExtractedTable(reuseTable);
      return;
    }
    console.debug("Extracting table using model " + tableModel);
    // just run extraction
    try {
      reuseTable = await new TableExtractor(
        this._model.getBatchClassIdentifier(),
        tableModel,
        this._treatNotFoundAsStale,
      ).extract(this._acceleratorDoc);
    } catch (e) {
      console.log(e);
    }

    if (!reuseTable) {
      console.warn(
        `Could not extract TABLE ${tableConfig.name} using matcher ${tableModel.getModelId()}`,
      );
      return;
    }
    // table could be either reuse or extraction model, we need to identify how to know what the

    // table model not found in cache, so set as an extraction value for the model
    if (reuseForMatcherIdx == -1) {
      this._acceleratorDoc.updateExtractedTable(reuseTable);
    } else {
      // reuse was found in cache, ensure the new index is incremented
      const newIndex = extractedTableData.getIndex();
      // newIndex++;
      reuseTable.status = "REUSE";
      reuseTable.name = extractedTableData.getName();
      // because tables are funky, always add this table as a reuse result
      extractedTableData.put(reuseTable);
      extractedTableData.setIndex(newIndex);
    }
  }
}
